import { ComponentPropsWithoutRef, ComponentPropsWithRef, ElementType, forwardRef, ReactNode } from 'react';

import Button, { buttonClasses } from '@mui/material/Button';
import { styled } from '@mui/material/styles';

import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';
import { ChevronRight } from '@o2/icons';

import { ButtonProps } from '../Button';
import { LinkProps } from '../Link';
import { ColorList } from '../theme/colorList';
import colors from '../theme/colors';

/**
 * Additional props shared between ownerState and the public API.
 * @internal
 */
export interface LinkBoxSharedProps {
  /**
   * What color to show the 1px solid border in.
   * @default 'gray'
   */
  borderColor?: ColorList | 'gray';

  /**
   * LinkBox size
   * @default 'medium'
   */
  size?: 'x-small' | 'small' | 'medium';

  /**
   * Background color.
   * @default 'transparent'
   */
  color?: 'internal' | 'white' | 'transparent';

  /**
   * Displays Chip over the top left corner and extends styles for absolute positioned Chip element.
   */
  chip?: ReactNode;
}

interface LinkBoxRootProps extends ButtonProps {
  ownerState: LinkBoxSharedProps;
}

const LinkBoxRoot = styled(Button, {
  name: 'O2Linkbox',
  slot: 'Root',
})<LinkBoxRootProps>(({ ownerState: { borderColor, color, size, chip }, theme }) => ({
  borderRadius: theme.shape.borderRadius,
  borderWidth: 1,
  justifyContent: 'flex-start',
  borderColor: !borderColor || borderColor === 'gray' ? colors.transparentGray[200] : theme.palette[borderColor].main,
  fontWeight: theme.typography.fontWeightRegular,
  textAlign: 'left',
  color: colors.transparentGray[900],
  [`&.${buttonClasses.root}`]: {
    whiteSpace: 'normal',
    ...(size === 'x-small' && {
      padding: theme.spacing(1, 2),
      fontSize: theme.typography.pxToRem(14),
      lineHeight: 1.3,
    }),
    ...(size === 'small' && {
      padding: theme.spacing(2),
      fontSize: theme.typography.pxToRem(14),
    }),
    ...((!size || size === 'medium') && {
      padding: theme.spacing(3),
      fontSize: theme.typography.pxToRem(16),
    }),
  },
  '& .MuiButton-startIcon': {
    color: colors.solidGray[600],
    marginRight: theme.spacing(1.5),
    '& > *': {
      fontSize: theme.typography.pxToRem(20),
      ...(size === 'x-small' && {
        fontSize: theme.typography.pxToRem(16),
      }),
      ...(size === 'small' && {
        fontSize: theme.typography.pxToRem(16),
      }),
    },
  },
  '& .MuiButton-endIcon': {
    display: 'grid',
    gridAutoFlow: 'column',
    gap: 16,
    marginLeft: 'auto',
    paddingLeft: 8,
    marginRight: `-${theme.spacing(1)}`,
    marginTop: `-${theme.spacing(2)}`,
    marginBottom: `-${theme.spacing(2)}`,
    color: colors.transparentGray[400],
    ...(size === 'x-small' && {
      gap: 12,
    }),
    ...(size === 'small' && {
      gap: 12,
    }),
    '& > *:nth-of-type(n)': {
      fontSize: theme.typography.pxToRem(24),
      ...(size === 'x-small' && {
        fontSize: theme.typography.pxToRem(18),
      }),
      ...(size === 'small' && {
        fontSize: theme.typography.pxToRem(18),
      }),
      transition: theme.transitions.create(['color'], { duration: theme.transitions.duration.short }),
    },
  },
  '&.MuiButton-iconSizeSmall.MuiButton-endIcon > *': {
    fontSize: theme.typography.pxToRem(16),
  },
  backgroundColor:
    color === 'internal'
      ? theme.palette.background.internal
      : color === 'white'
      ? theme.palette.common.white
      : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.background.primary,
    [`& .${buttonClasses.endIcon} > *:last-of-type`]: {
      color: theme.palette.primary.main,
    },
    borderWidth: 1,
  },
  ...(chip && {
    position: 'relative',
    marginTop: 8,
  }),
  '& > .MuiChip-root:first-of-type': {
    ...(chip &&
      color === 'internal' && {
        backgroundColor: theme.palette.background.internal,
      }),
  },
  '&:hover > .MuiChip-root:first-of-type': {
    ...(chip && {
      backgroundColor: theme.palette.background.primary,
    }),
  },
  '.MuiTouchRipple-root': {
    borderRadius: '5px',
  },
}));

interface StyledChevronProps {
  hidden?: boolean;
}

const StyledChevron = styled(ChevronRight, {
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'hidden',
})<StyledChevronProps>(({ hidden }) => ({
  ...(hidden && {
    visibility: 'hidden',
  }),
}));

export type LinkBoxProps<T extends ElementType = 'button'> = Omit<ButtonProps<T>, 'size' | 'color'> &
  LinkBoxSharedProps & {
    children: ReactNode;
    /**
     * Defaults to ChevronRight.
     */
    endIcon?: ReactNode;
    fullWidth?: boolean;
    hideIcon?: boolean;
    target?: LinkProps['target'];
    chip?: ReactNode;

    /**
     * What component to render this component as (inheriting props).
     * Useful when combining with e.g. react-router.
     */
    component?: T;

    ref?: ComponentPropsWithRef<T>['ref'];
  } & ComponentPropsWithoutRef<T>;

const LinkBox = forwardRef(
  <T extends ElementType = 'button'>(
    {
      borderColor = 'gray',
      children,
      color,
      endIcon,
      hideIcon = false,
      size = 'medium',
      chip,
      ...other
    }: LinkBoxProps<T>,
    ref: LinkBoxProps<T>['ref']
  ) => (
    <LinkBoxRoot
      size={size === 'x-small' ? 'small' : size}
      variant="outlined"
      color="inherit"
      endIcon={hideIcon ? <StyledChevron hidden /> : endIcon || <StyledChevron />}
      ref={ref}
      ownerState={{ color, borderColor, size, chip }}
      {...other}
    >
      {chip}
      {children}
    </LinkBoxRoot>
  )
);

LinkBox.displayName = 'LinkBox';

export default LinkBox;
